import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClockCircleOutlined } from '@ant-design/icons';

import { Button, DatePicker, Flex, Input, Select, Switch, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import { useFormik } from 'formik';

import EditIcon from 'assets/images/icons/edit.svg?react';
import LightningIcon from 'assets/images/icons/lightning.svg?react';
import { EditPostPageState } from 'entities/Post';
import { autoDeletionAfterDayjsValues, autoDeletionAfterOptions } from 'entities/Post/consts';
import { OPTIONS_MAX_HEIGHT_NOTIFICATIONS } from 'features/Filters';
import { AutoDeletionAfterEnum } from 'features/Planner';
import { useChannelOptions } from 'shared/hooks';
import { Content, InputClearIcon } from 'shared/ui';
import { formatDate, isAndroid, isIOS, range } from 'shared/utils';

import styles from '../PostForm.module.scss';

interface IPublishingSettingsPartProps extends Pick<ReturnType<typeof useFormik>, 'setFieldValue' | 'values'> {
	editingTime: Date | null;
	setPageState: Dispatch<SetStateAction<EditPostPageState>>;
}

export const PublishingSettingsPart: FC<IPublishingSettingsPartProps> = ({
	editingTime,
	setFieldValue,
	setPageState,
	values,
}) => {
	const { t } = useTranslation();

	const { channelOptions, isFetching } = useChannelOptions({ activeOnly: true });

	const [deletedTime, setDeletedTime] = useState<string>('');

	useEffect(() => {
		let deletedTime = '';
		if (values.auto_deletion_after && values.auto_deletion_after !== AutoDeletionAfterEnum.Never) {
			const publishData = values.date
				? values.date.set('hour', values.time?.hour() || 0).set('minute', values.time?.minute() || 0)
				: dayjs();
			deletedTime = publishData
				.add(
					autoDeletionAfterDayjsValues[values.auto_deletion_after as AutoDeletionAfterEnum].value,
					autoDeletionAfterDayjsValues[values.auto_deletion_after as AutoDeletionAfterEnum].unit,
				)
				.format('DD MMM, HH:mm');
		}
		setDeletedTime(deletedTime);
	}, [values.auto_deletion_after, values.date, values.time, values.publishNow]);

	const handleEditPostClick = () => {
		setPageState(EditPostPageState.POST_EDITOR);
	};

	const [currentDate, setCurrentDate] = useState<dayjs.Dayjs>(dayjs());

	useEffect(() => {
		const interval = setInterval(() => {
			const newCurrentDate = dayjs().add(1, 'minute').set('second', 1);
			setCurrentDate(newCurrentDate);

			if (values.date?.isBefore(newCurrentDate, 'day')) {
				setFieldValue('date', newCurrentDate);
			}

			if (values.date?.isSame(newCurrentDate, 'day') && values.time?.isBefore(newCurrentDate, 'minute')) {
				setFieldValue('time', newCurrentDate);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [values.time, values.date]); // eslint-disable-line react-hooks/exhaustive-deps

	const disabledTime = () => ({
		disabledHours: () => {
			if (values.date?.isBefore(currentDate, 'day')) {
				return range(0, 23);
			}

			if (values.date?.isSame(currentDate, 'day')) {
				return range(0, currentDate.hour() - 1);
			}

			return [];
		},
		disabledMinutes: (hour: number) => {
			if (values.date?.isBefore(currentDate, 'day')) {
				return range(0, 59);
			}

			if (values.date?.isSame(currentDate, 'day') && hour < currentDate.hour()) {
				return range(0, 59);
			}

			if (values.date?.isSame(currentDate, 'day') && hour === currentDate.hour()) {
				return range(0, currentDate.minute() - 1);
			}

			return [];
		},
	});

	const minTime = values.date?.isSame(currentDate, 'day') ? currentDate.format('HH:mm') : undefined;

	return (
		<Content>
			<Flex vertical gap={8} className={styles.postForm}>
				<div className={styles.postForm__card}>
					<Typography.Title level={5}>{t('Канал')}</Typography.Title>
					<Select
						value={values.channel_ids}
						onChange={(value) => {
							setFieldValue('channel_ids', value);
						}}
						options={channelOptions}
						placeholder={t('Выберите канал')}
						size="large"
						showSearch
						optionFilterProp="search"
						allowClear={{ clearIcon: <InputClearIcon /> }}
						listHeight={OPTIONS_MAX_HEIGHT_NOTIFICATIONS}
						style={{ width: '100%' }}
						loading={isFetching}
						disabled={isFetching}
					/>
				</div>
				<div className={styles.postForm__card}>
					<Typography.Title level={5}>{t('Дата и время')}</Typography.Title>
					<Flex gap={8}>
						<DatePicker
							placeholder={t('Выберите дату')}
							disabled={values.publishNow}
							className={styles.postForm__fx}
							size="large"
							value={values.date}
							onChange={(value) => {
								setFieldValue('date', value.set('second', 1));
							}}
							minDate={dayjs()}
							format="DD.MM.YYYY"
						/>
						{isIOS || isAndroid ? (
							<Input
								type="time"
								defaultValue={values.time?.format('HH:mm')}
								placeholder={t('Выберите время')}
								disabled={values.publishNow}
								className={styles.postForm__fx}
								size="large"
								value={values.time?.format('HH:mm')}
								onChange={(value) => {
									setFieldValue('time', dayjs(value.target.value, 'HH:mm').set('second', 1));
								}}
								suffix={<ClockCircleOutlined className={styles.clock} />}
								min={minTime}
							/>
						) : (
							<TimePicker
								placeholder={t('Выберите время')}
								disabled={values.publishNow}
								className={styles.postForm__fx}
								format={'HH:mm'}
								size="large"
								value={values.time}
								onChange={(value) => {
									setFieldValue('time', value.set('second', 1));
								}}
								inputReadOnly
								popupClassName={styles.postForm__timePickerPopup}
								disabledTime={disabledTime}
							/>
						)}
					</Flex>
					<label className={styles.postForm__switchBlock}>
						<LightningIcon color="#d89614" width={28} height={28} />
						<Typography.Text className={styles.postForm__w100}>{t('Опубликовать прямо сейчас')}</Typography.Text>
						<Switch
							value={values.publishNow}
							onChange={(value) => {
								setFieldValue('publishNow', value);
							}}
						/>
					</label>
					<Typography.Paragraph className={styles.postForm__label}>{t('Удалить автоматически')}</Typography.Paragraph>
					<Select
						placeholder={t('Никогда')}
						options={autoDeletionAfterOptions}
						className={styles.postForm__w100}
						size="large"
						value={values.auto_deletion_after}
						onChange={(value) => setFieldValue('auto_deletion_after', value)}
					/>
					{!!deletedTime && (
						<Typography.Paragraph type="secondary" className={styles.postForm__subText}>
							{t('Будет удалено:')} {deletedTime}
						</Typography.Paragraph>
					)}
				</div>
				<Button
					onClick={handleEditPostClick}
					htmlType="button"
					size="large"
					variant="outlined"
					color="primary"
					icon={<EditIcon />}
					disabled={!values.channel_ids}>
					{t('Редактировать пост')}
				</Button>
				{!!editingTime && (
					<Typography.Text type="secondary">
						{t('Отредактирован')}: {formatDate(editingTime, 'humanoid')}
					</Typography.Text>
				)}
			</Flex>
		</Content>
	);
};
